import { Dayjs } from 'dayjs';

export type SortOrder = 'asc' | 'desc';

export interface Group {
  id: string;
  name: string;
  createdAt?: string;
  updatedAt?: string;
}

export interface User {
  id: string;
  _id: string;
  firstName: string;
  lastName: string;
  email: string;
  phone?: string;
  isVerified: boolean;
  profilePhoto?: string;
  zoneInfo?: string;
  role: UserRole;
  // TCX-507: consultant groups are now populated Group refs ({id,name}).
  groups?: Group[];
  createdAt: string;
  updatedAt: string;
  status: boolean;
  address?: string;
  country?: string;
  city?: string;
  stateProvince?: string;
  primaryPaymentMethod?: string;
  profileUrl?: string;
  publicUrl?: string;
  isDeleted?: boolean;
  deletedAt?: string;
  stripeCustomerId?: string;
  isActive?: boolean;
  franchise?: string;
  isDeactivated?: boolean;
  tokenActivation?: string;
  tokenActivationExpiresAt?: string;
  priority?: number;
  smsConsent?: boolean;
  trialDays?: number;
  billingAmt?: number;
  subscriptionInfo?: {
    amount?: number;
    currency?: string;
    interval?: string;
    interval_count?: number;
    livemode?: boolean;
    metadata?: Record<string, any>;
    object?: string;
    payment_method_types?: string[];
    status?: string;
    // TCX-224: live Stripe status normalized like franchise.stripeSubscriptionStatus
    // (active/trialing/grace/...). Preferred over the stored status so the UI
    // doesn't lag a missed subscription webhook.
    normalizedStatus?: string;
    // TCX-224: live cancel-at-period-end, preferred over the stored flag for the
    // Reactivate/Cancel UI (same webhook-lag reason as normalizedStatus).
    cancelAtPeriodEnd?: boolean;
    // TCX-455: Stripe `cancel_at` (unix seconds) — set for both end-of-period
    // and custom-date scheduled cancellations. The franchisor "Cancelled" card
    // detects this and uses it as the "Access until" date.
    cancelAt?: number | null;
    tax_percent?: number;
    trial_end?: number;
    trial_period_days?: number;
    nextBillingDate?: null | Date;
    // TCX-659: 12-month commitment renewal/term-end date (ISO string), anchored
    // to the subscription start_date. Surfaced as the franchisor "Renews on" row.
    termRenewalDate?: string | null;
    name?: string;
    description?: string | null;
    // TCX-713: 'connect' | 'full' — plan tier derived server-side from the
    // Stripe price metadata (same helper SubscriptionGuard uses). Gates the
    // Message / Book-a-Meeting affordances for TCX Connect subscribers.
    tier?: string | null;
  };
  // TCX-668: whether this user must re-accept the latest legal documents, plus
  // the current versions for display in the re-acceptance modal. Attached by
  // the profile endpoint for consultant/franchisor users.
  legalReacceptance?: {
    required: boolean;
    privacy: { version: number; publishedAt: string | null } | null;
    terms: {
      audience: 'consultant' | 'franchisor';
      version: number;
      publishedAt: string | null;
    } | null;
  };
}

export interface UserCalendar {
  provider_name: string;
  profile_id: string;
  profile_name: string;
  calendar_id: string;
  calendar_name: string;
  calendar_readonly: boolean;
  calendar_deleted: boolean;
  calendar_primary: boolean;
  calendar_integrated_conferencing_available: boolean;
  calendar_attachments_available: boolean;
  permission_level: boolean;
}

export interface Consultant extends User {
  company?: string;
  experienceLevel?: number;
  rate: number;
  bio?: string;
  bookingLink?: string;
  bookingLinkClear?: boolean;
  // TCX-665: opt-in minimum advance-notice window (hours) before a booking.
  // null/undefined = off. Allowed: 1/2/4/24/48.
  minimumAdvanceHours?: number | null;
  minimumAdvanceHoursClear?: boolean;
  cronofyAccessToken: string;
  cronofyAccountId: string;
  cronofyConferencingProfileId: string;
  cronofyRefreshToken: string;
  cronofyTokenExpiresAt: string;
  cronofyCalendarId: string;
  industrySpecialty?: string[];
  specialty?: string[];
  region?: string[];
  calendars?: UserCalendar[];
  consultantProfileUpdateTimeStamp?: string;

  // DELETE
  position?: string;
  averageRating?: number;
  totalRatedUsers?: number;
  totalReviews?: number;
  isProMember?: boolean;
  isNewToTCX?: boolean;
  isEmergingPro?: boolean;
  meetingsCount?: number;
  lastMeetingAt?: string | null;
}

export interface ConsultantNote {
  id: string;
  text: string;
  createdAt: string;
}

export interface FranchiseDocument {
  file: string;
  publicName: string;
  size: number;
}

export interface FranchiseVideo {
  title?: string;
  url: string;
  linkText?: string;
}

export interface FinancialBreakdown {
  conceptName?: string;
  financialMetrics?: {
    cogs?: {
      description?: string;
      value?: number;
    };
    ebitda?: {
      description?: string;
      margin?: string;
      value?: number;
    };
    grossProfit?: {
      description?: string;
      grossMargin?: string;
      value?: number;
    };
    operatingExpenses?: {
      breakdown?: Record<string, any>;
      description?: string;
      total?: number;
    };
    revenue?: {
      description?: string;
      value?: number;
    };
  };
  matureGrowth?: {
    description?: string;
    value?: number;
  };
  totalInvestment?: {
    max?: number;
    min?: number;
  };
}

// TCX-866: royalty, adFee, and every commission/internationalCommissions leaf
// moved from a bare scalar to this nested object on the backend, keeping the
// same key (e.g. `franchise.royalty` was `'6'`, is now `{ type: 'percent',
// value: '6' }`). `value` is always a string for all three `type`s; `note` is
// optional and omitted (not `''`) when empty. Rendering the `type` unit affix
// and the `note` field is TCX-867/868 — this package only unwraps `.value`.
export type FeeValueType = 'percent' | 'currency_usd' | 'open_text';

export interface FeeValue {
  type?: FeeValueType;
  value?: string;
  note?: string;
}

// Mirrors BillingRecipientSource in the backend's stripe.service.ts: which
// tier of the invoice-recipient chain produced `emails`.
export type BillingRecipientSource =
  | 'billingContacts'
  | 'billingAddress'
  | 'activatedBy'
  | 'none';

export interface BillingRecipients {
  emails: string[];
  source: BillingRecipientSource;
}

// TCX-507: User.groups is now Group[] (consultant refs); Franchise keeps its own
// string[] groups, so omit the inherited field to avoid the type clash.
export interface Franchise extends Omit<User, 'groups'> {
  userId: string;
  company?: string;
  overview?: string;
  categories?: string[];
  groups?: string[];
  established?: Date | Dayjs;
  isActivated?: boolean;
  startedFranchising?: Date | Dayjs;
  franchisedUnits?: number;
  corporateUnits?: number;
  franchisesInCanada?: boolean;
  otherInternationalFranchises?: boolean;
  isFreeListing?: boolean;
  // TCX-815: admin "Offer TCX Connect" toggle (Franchise Details). When true,
  // the hidden $99 Connect plan is surfaced on this franchisor's subscription
  // page. One-shot — the backend resets it to false once any plan activates.
  connectOffered?: boolean;
  statesRegisteredIn?: string[];
  photos?: string[];
  videos?: FranchiseVideo[];
  documents?: FranchiseDocument[];
  fddFiles?: FranchiseDocument[];
  logo?: string;
  franchisors?: User[];
  billingAddress?: string;
  billingContacts?: string[];
  // Admin detail only (TCX-875): who actually receives this franchise's
  // invoices, from the backend's StripeService.resolveFranchiseBillingRecipients.
  billingRecipients?: BillingRecipients;
  // False when the Stripe customer email is known to be out of sync with
  // billingAddress (see franchise.schema.ts) — the daily reconciler flips it
  // back once the sync succeeds.
  stripeBillingEmailSynced?: boolean;
  profileUpdateTimeStamp?: string;
  franchiseUpdatedTimeStamp?: string;
  // Investment Profile
  franchiseFee?: string;
  minNetWorth?: number;
  cashRequiredMin?: number;
  cashRequiredMax?: number;
  // TCX-866: was a bare scalar string; now { type, value, note }. Read sites
  // unwrap `.value`; rendering `type`/`note` is TCX-867/868.
  royalty?: FeeValue;
  adFee?: FeeValue;
  item19?: boolean;
  avgNumberOfStaff?: string;
  visaCandidates?: boolean;
  absenteeModel?: boolean;
  semiAbsenteeModel?: boolean;
  b2b?: boolean;
  homeBased?: boolean;
  masterFranchiseOpportunities?: boolean;
  veteranDiscount?: boolean;
  specialDiscountPrograms?: string;
  sbaEligible?: boolean;
  e2VisaFriendly?: boolean;
  isIndependentConsultantFriendly?: boolean;
  isTcxMember?: boolean;
  // TCX-740: per-franchisor Universal Referral Terms feature gate.
  isUniversalReferralTermsEnabled?: boolean;
  standing?: FranchiseStandingStatus;

  // Training and Support
  financialAssistance?: boolean;
  financialAssistanceDetails?: string;
  siteSelectionAssistance?: boolean;
  leaseNegotiationAssistance?: boolean;
  recruitmentAssistance?: boolean;
  coopAdvertising?: boolean;
  training?: string;
  dayInTheLifeOfTheOwner?: string;
  whoIsAnIdealCandidate?: string;

  // Contact Info
  corporateAddress?: string;
  corporateWebsite?: string;
  premiumDashboardUrl?: string;
  workPhone?: string;

  // Commissions
  // TCX-866: every leaf is now a FeeValue instead of a bare scalar. The
  // previous `number` typing here was already wrong — these values are
  // strings (e.g. "15000"); `value` is always a string.
  commissions?: {
    singleUnit?: FeeValue;
    multipleUnit?: FeeValue;
    areaMaster?: FeeValue;
    veteran?: FeeValue;
    resale?: FeeValue;
  };

  internationalCommissions?: {
    singleUnit?: FeeValue;
    multipleUnit?: FeeValue;
    areaMaster?: FeeValue;
    resale?: FeeValue;
    // TCX-866: new field — did not exist in this package before.
    veteran?: FeeValue;
  };

  // Stripe Subscription
  stripeSubscriptionId?: string;
  stripeSubscriptionStatus?: string;
  stripeSubscriptionTrialStart?: Date;
  stripeSubscriptionTrialEnd?: Date;
  stripeSubscriptionCurrentPeriodStart?: Date;
  stripeSubscriptionCurrentPeriodEnd?: Date;
  stripeSubscriptionCancelAtPeriodEnd?: boolean;
  stripeSubscriptionNew?: boolean;

  // TCX-455: franchisor cancellation-request state (no Stripe action taken)
  cancellationRequested?: boolean;
  // Serialized as an ISO string over the API (JSON has no Date type) — parse
  // with new Date(...) at the use site rather than calling Date methods on it.
  cancellationRequestedAt?: string | null;
  // Stored HTML-sanitized on the backend (safe to render), but still escape on
  // render as defense-in-depth if this is ever surfaced in the UI.
  cancellationRequestReason?: string;

  // TCX-839: franchisor Connect→premium upgrade-request state (no Stripe
  // action taken — the admin performs the plan change by hand; the backend
  // clears these via webhook once the plan actually changes). PlanName /
  // PlanInterval are a snapshot of the requested target plan taken at request
  // time, so the pending card can name it without a live plans fetch.
  upgradeRequested?: boolean;
  upgradeRequestedAt?: string | null;
  upgradeRequestedPlanId?: string;
  upgradeRequestedPlanName?: string;
  upgradeRequestedPlanInterval?: string;
  upgradeRequestedFromPriceId?: string;

  // Fields from original interface that weren't in schema
  averageRevenue?: number;
  minInitialInvestment?: number;
  maxInitialInvestment?: number;
  // subscriptionStatus?: string;
  favoriteConsultantIds?: string[];
  paymentMethod?: string;

  //paymentMethodCount
  totalPaymentMethods: number;

  users: User[];

  chartData?: [];

  financialBreakdown?: FinancialBreakdown | null;

  franchableName?: string;
  franchableNameUpdatedAt?: Date;
}

export interface Session {
  id: string;
  _id: string;
  userId: string;
  consultantId: string;
  franchiseId: string;
  status: string;
  startTime: string;
  endTime: string;
  createdAt: string;
  updatedAt: string;
}

export type GeneralUser = User & Consultant & Franchise;

export enum UserRole {
  FRANCHISEE = 'franchisor',
  CONSULTANT = 'consultant',
  ADMIN = 'admin',
  SUPER_ADMIN = 'superAdmin',
}

enum ExperienceLevelEnum {
  ANY = 'ANY',
  UNDER_ONE_YEAR = 'UNDER 1 YEAR',
  ONE_THREE_YEARS = '1-3 YEARS',
  THREE_FIVE_YEARS = '3-5 YEARS',
  FIVE_TEN_YEARS = '5-10 YEARS',
  TEN_PLUS_YEARS = '10+ YEARS',
}

enum LastMeetingBookedEnum {
  ANY = 'ANY',
  LAST_7_DAYS = 'LAST 7 DAYS',
  LAST_30_DAYS = 'LAST 30 DAYS',
  GRATER_THAN_30_DAYS = '30+ DAYS AGO',
  NO_MEETINGS = 'NO MEETINGS',
}

export enum StripeSubscriptionStatus {
  // Raw Stripe values we may still persist
  ACTIVE = 'active',
  TRIALING = 'trialing',
  PAST_DUE = 'past_due',
  UNPAID = 'unpaid',
  INCOMPLETE = 'incomplete',
  INCOMPLETE_EXPIRED = 'incomplete_expired',

  // Normalized / internal values
  GRACE = 'grace',
  CANCELED = 'canceled',
  INVALID = 'invalid',
  NOT_ACTIVE = 'not_active',
}

export enum AverageRevenueEnum {
  ANY = 'Any',
  LESS_THAN_200K = 'Less than $200,000',
  BETWEEN_200K_300K = '$200,000 - $300,000',
  BETWEEN_300K_400K = '$300,000 - $400,000',
  GRATER_THAN_400K = 'More than $400,000',
}

export enum FranchiseUnitsEnum {
  ANY = 'Any',
  UP_TO_20 = '0 - 20',
  BETWEEN_21_100 = '21 - 100',
  BETWEEN_101_300 = '101 - 300',
  BETWEEN_301_600 = '301 - 600',
  BETWEEN_601_1000 = '601 - 1,000',
  OVER_1000 = '1,000+',
}

export interface LastMeetingBooked {
  label: LastMeetingBookedEnum;
  minValue: number | null;
  maxValue: number | null;
}

export interface ExperienceLevel {
  label: ExperienceLevelEnum;
  minValue: number | null;
  maxValue: number | null;
}

export interface AverageRevenue {
  label: AverageRevenueEnum;
  minValue: number | null;
  maxValue: number | null;
}

export interface FranchiseUnits {
  label: FranchiseUnitsEnum;
  minValue: number | null;
  maxValue: number | null;
}

export enum Groups {
  FOOD_BEVERAGE = 'Food & Beverage',
  FINANCE = 'Finance',
  IT_TECHNOLOGY = 'IT/Technology',
  REAL_ESTATE = 'Real Estate',
  AUTOMOTIVE = 'Automotive',
  HEALTH_WELLNESS = 'Health & Wellness',
  EDUCATION = 'Education',
  RETAIL = 'Retail',
  OTHER = 'Other',
}

export interface ConsultantFilters {
  maxRate: number | undefined;
  minRate: number | undefined;
  minValue: number | null | undefined;
  maxValue: number | null | undefined;
  minExperience: number | null | undefined;
  maxExperience: number | null | undefined;
  ratingMin: number | undefined;
  ratingMax: number | undefined;
  group: string[] | undefined;
  myGroups: boolean | undefined;
  industrySpecialty: string[] | undefined;
  region: string[] | undefined;
  sbaEligible?: boolean;
  e2VisaFriendly?: boolean;
  ratings?: number[] | null | undefined;
  isProMember?: boolean;
  isEmergingPro?: boolean;
  relationshipStatus?: number[] | null;
}

export interface FranchisorFilters {
  minRevenue: number | null | undefined;
  maxRevenue: number | null | undefined;
  minUnits: number | null | undefined;
  maxUnits: number | null | undefined;
  group: string[] | undefined;
  item19: boolean | undefined;
  b2b: boolean | undefined;
  homeBased: boolean | undefined;
  semiAbsenteeModel: boolean | undefined;
  fddFiles: boolean | undefined;
  sbaEligible?: boolean;
  e2VisaFriendly?: boolean;
  veteranDiscount?: boolean;
  tcxMembersOnly?: boolean | null;
  independentConsultantFriendly?: boolean | null;
  registrationState: string[] | undefined;
}

export interface BookingEvent {
  createdAt?: string;
  calendarId: string;
  consultantId: string;
  end: string;
  eventId: string;
  franchisorId: string;
  meetingLink: string;
  start: string;
  status: string;
  duration: string;
  _id: string;
}

export interface Booking {
  _id: string;
  review?: { rating: number; comment?: string }[];
  id: string;
  status: string;
  consultant: {
    id: string;
    firstName: string;
    lastName: string;
    bookingLink: string;
    bio: string;
    groups: string[]; // TCX-507: session pipelines now return group names
    experienceLevel: number;
    rate: number;
    profilePhoto?: string;
    isVerified: boolean;
  };
  franchisor: {
    id: string;
    firstName: string;
    lastName: string;
    company: string;
    country: string;
    averageRevenue: number;
    minInitialInvestment: number;
    maxInitialInvestment: number;
    city: string;
    profilePhoto: string;
    isVerified: boolean;
  };
  //TODO: FIX COLLISION WITH FRANCHISE
  franchise: {
    id: string;
    firstName: string;
    lastName: string;
    company: string;
    country: string;
    averageRevenue: number;
    minInitialInvestment: number;
    maxInitialInvestment: number;
    city: string;
    profilePhoto: string;
    isVerified: boolean;
  };
  reviewStatus: BookingReviewStatus;
  duration: number;
  bookingLink: string;
  start: string;
  end: string;
  totalAmount?: number;
  // TCX-955: the price snapshotted at booking time, and the value every biller
  // reads (`deleteSession`/`requestReschedule` as `paidAmount ?? 0`, the weekly
  // cron as `paidAmount * 100`). All three session pipelines already project
  // it; the FE simply never declared it, which is why the fee expressions fell
  // back to the consultant's mutable current rate. Nullable, not merely
  // optional: the aggregation projects the field whether or not the document
  // carries it, so the FE sees `null` as well as `undefined` and the helpers in
  // `helpers/booking-fee.ts` must treat the two identically.
  paidAmount?: number | null;
  createdAt: string;
  reschedule?: {
    state: 'requested' | 'approved' | 'denied' | 'rejected';
    originalStart: string;
    originalEnd: string;
    newStart: string;
    newEnd: string;
    feeAmount: number;
    feeOwed?: boolean;
  };
  lateCancellation?: {
    feeOwed: boolean;
    feeAmount: number;
  };
}

export enum BookingReviewStatus {
  PENDING = 'pending',
  REQUESTED = 'requested',
  COMPLETED = 'completed',
}

export interface Payment {
  id: string;
  value: number;
  status: PaymentStatus;
  purpose: string;
  date: Date;
}

export interface Notification {
  id: string;
  message: string;
  createdAt: Date;
  isRead: boolean;
  avatar: string;
  firstName: string;
  lastName: string;
}

export interface IReview {
  id: string;
  rating: number;
  userId: string;
  sessionId: string;
  comment: string;
}

export enum BookingStatus {
  PENDING = 'pending',
  COMPLETED = 'completed',
  DECLINED = 'declined',
}

export enum BookingStatusEnum {
  FUTURE = 'future',
  PENDING = 'pending',
  FINISHED = 'completed',
  DECLINED = 'declined',
}

export enum PaymentStatus {
  FUTURE = 'future',
  PENDING = 'pending',
  COMPLETED = 'completed',
}

export interface BookingAdmin {
  id: string;
  _id: string;
  consultantId: {
    id: string;
    firstName: string;
    lastName: string;
    rate: number;
  };
  franchiseId: {
    id: string;
    firstName: string;
    lastName: string;
  };
  duration: number;
  bookingLink: string;
  status: BookingStatus;
  isPaid: boolean;
  dateTime: Date | null;
  consultantName: string;
  franchiseName: string;
  consultantCompany: string;
  franchiseBrand: string;
  consultantRate: number;
  updatedAt: Date;
  createdAt: Date;
  paidAmount: number;
  start: Date;
  markedAsCompletedAt: Date;
  review: IReview[];
  // Consultant-detail only (TCX-574): the comment "left by the consultant"
  // (consultant→franchisor), surfaced separately from `review` (franchisor→
  // consultant) so the Rate and Additional Feedback columns can read opposite
  // review directions on the same row.
  additionalFeedback?: string;
  reviewStatus: BookingReviewStatus;
}

export interface PaymentsAdmin {
  id: string;
  consultant: {
    id: string;
    firstName: string;
    lastName: string;
  };
  franchise: {
    id: string;
    company: string;
    firstName: string;
    lastName: string;
  };
  createdAt: string;
  start?: string | null;
  amount: number;
  paidAmount?: number | null;
  transactionId?: string | null;
  isPaid: boolean;
  paidAt: string | null;
  isRefunded: boolean;
  refundedAt: string | null;
  status: PaymentAdminStatus;
  markedAsCompletedAt: string;
  updatedAt: string;
}

export enum PaymentAdminStatus {
  UNCONFIRMED = 'unconfirmed',
  CONFIRMED = 'confirmed',
  COMPLETED = 'completed',
  DECLINED = 'declined',
}

export interface Promotion {
  id: string;
  name: string;
  description: string;
  effect: string;
  affected: string;
  status: string;
  startDate: Date;
  endDate: Date;
}

export interface PromotionSimplified {
  key: string;
  title: string;
  description: string;
}

export interface ChatMessage {
  _id?: string;
  sender: {
    _id: string;
    fullName: string;
  };
  isRead: boolean;
  attachments?: {
    filename: string;
    url: string;
    mimetype?: string;
    size?: number;
  }[];
  imageUrl?: string;
  content?: string;
  createdAt: Date;
  // TCX-903: written by the TCX team on the brand's behalf. Renders the
  // "Sent by Admin" chip; absent on every message sent before TCX-903.
  sentByAdmin?: boolean;
  // TCX-903 follow-up: WhatsApp-style reply quoting. The `_id` of another
  // message in the SAME conversation this one replies to. No denormalised
  // copy of the quoted text is stored server-side — the client resolves it
  // from the messages it already has (see chat-content/index.tsx), and it
  // may legitimately resolve to nothing when the quoted message falls
  // outside the loaded window (getChats projects messages with $slice).
  replyTo?: string;
}

export interface ChatParticipant {
  id: string;
  firstName: string;
  lastName: string;
  profilePhoto?: string;
  franchise?: string;
  company?: string;
  role: UserRole;
  logo?: string;
  cronofyCalendarId?: string;
  isFreeListing?: boolean;
}

// Territory Check / Candidate Registration cards (TCX-709/711). A card
// annotates one chat message (joined via `message` === ChatMessage._id);
// the DB stays label-agnostic — display labels live in the engagement-card
// component's meta map.
export type EngagementKind = 'territory_check' | 'candidate_registration';
export type EngagementState = 'pending' | 'responded' | 'withdrawn';
export type EngagementOutcome =
  | 'available'
  | 'not_available'
  | 'accepted'
  | 'declined';

export interface EngagementAttachment {
  filename: string;
  url: string;
  mimetype?: string;
  size?: number;
}

export interface ChatEngagement {
  _id: string;
  kind: EngagementKind;
  message: string | null;
  state: EngagementState;
  body: string;
  candidate?: {
    fullName: string;
    comments?: string;
    attachments?: EngagementAttachment[];
    consent: boolean;
  };
  outcome?: EngagementOutcome | null;
  responseBody?: string | null;
  outcomeAt?: string | null;
  // Immutable first-response time (TCX-711) — anchors the 72h edit window;
  // distinct from outcomeAt, which moves to the latest decision.
  respondedAt?: string | null;
  // Franchisor edited their response after first submitting it (TCX-711) —
  // distinct from `edited`, which is the consultant editing their submission.
  responseEdited?: boolean;
  responseEditedAt?: string | null;
  // TCX-903: this card was answered by the TCX team, not by the franchisor.
  respondedByAdmin?: boolean;
  submittedAt: string;
  edited: boolean;
  editedAt?: string | null;
  deliveryChannel: 'in_app' | 'email';
  createdAt: string;
}

export interface Chat {
  _id: string;
  participants: ChatParticipant[];
  unreadMessages: number;
  messages: ChatMessage[];
  engagements?: ChatEngagement[];
  hasTerritoryCheck?: boolean;
  hasCandidateRegistration?: boolean;
  createdAt: Date;
  updatedAt: Date;
}

export interface DashboardData {
  franchises: {
    total: number;
    lastMonth: number;
    ratio: number;
    eachMonth: number[];
  };
  consultants: {
    total: number;
    lastMonth: number;
    ratio: number;
    eachMonth: number[];
  };
  sessions: {
    total: number;
    lastMonth: number;
    ratio: number;
    eachMonth: number[];
  };
  amountPaid: {
    total: number;
    lastMonth: number;
    ratio: number;
    eachMonth: number[];
  };
  latestSessions: {
    limit: number;
    page: number;
    total: number;
    payments: {
      id: string;
      consultant: {
        id: string;
        firstName: string;
        lastName: string;
      };
      franchise: {
        id: string;
        firstName: string;
        lastName: string;
      };
      createdAt: Date;
      amount: number;
      isPaid: boolean;
      status: string;
    }[];
  };
}

export interface PaymentMethod {
  createdAt: Date;
  updatedAt: Date;
  id: string;
  isPrimary: boolean;
  last4: string;
  expMonth: string;
  expYear: string;
  stripePaymentMethodId: string;
  stripePaymentMethodType: string;
}

export interface CardPreviewData {
  brand: string;
  last4: string;
  expiry: string;
  id: string;
  paymentId: string;
  isPrimary: boolean;
}

export enum ReviewsStatus {
  FUTURE = 'future',
  PENDING = 'pending',
  COMPLETED = 'completed',
  DECLINED = 'declined',
}

export enum BrandNameStatus {
  APPROVED = 'approved',
  PENDING = 'pending',
  REJECTED = 'rejected',
}

export enum ReferenceStatus {
  APPROVED = 'approved',
  PENDING = 'pending',
  REJECTED = 'rejected',
}

export enum FranchiseStandingStatus {
  GOOD_STANDING = 'good-standing',
  IN_REVIEW = 'in-review',
  OUTSTANDING_DISPUTE = 'outstanding-dispute',
}

export interface BrandName {
  id: string;
  _id: string;
  name: string;
  status: BrandNameStatus;
  userRegisteredName: string;
  userRegisteredEmail: string;
  createdAt: Date;
  updatedAt: Date;
}

export interface IMostFavoriteSearch {
  id: string;
  title: string;
  description: string;
}

declare global {
  interface Window {
    Appcues?: {
      identify: (userId: string, properties?: Record<string, any>) => void;
    };
  }
}

export interface ConsultantReference {
  name: string;
  userId: {
    firstName: string;
    lastName: string;
  };
  company: string;
  email: string;
  status: 'pending' | 'active' | 'inactive'; // restrict to known statuses
  phone: string;
  createdAt: string; // ISO date string
  updatedAt: string; // ISO date string
  id: string;
}

export interface ConsultantReferenceResponse {
  references: ConsultantReference[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

// The naming is kept as "Plan" for backward compatibility in the frontend
export interface Plan {
  id: string;
  object: string;
  active: boolean;
  billing_scheme: string;
  created: number;
  currency: string;
  custom_unit_amount: any;
  livemode: boolean;
  lookup_key: string | null;
  metadata: Record<string, string>;
  nickname: string | null;
  product: string;
  recurring: {
    aggregate_usage: string | null;
    interval: 'day' | 'week' | 'month' | 'year';
    interval_count: number;
    meter: string | null;
    trial_period_days: number | null;
    usage_type: string;
  };
  tax_behavior: string;
  tiers_mode: string | null;
  transform_quantity: any;
  type: string;
  unit_amount: number;
  unit_amount_decimal: string;
}

// TCX-903 Free Listing Management (Admin Control).
export type FreeListingRequestType =
  | 'territory_check'
  | 'candidate_registration'
  | 'message';

export type FreeListingRequestStatus = 'pending' | 'responded';

export interface FreeListingRequestRow {
  id: string;
  rowType: 'engagement' | 'message';
  requestType: FreeListingRequestType;
  status: FreeListingRequestStatus;
  date: string;
  chatId: string;
  // The anchor the backend's messageBranch computed this row from — for a
  // 'message' row, the last non-annotated message in the thread; the admin
  // drawer (RequestPanel.tsx) climbs `replyTo` from here to scope itself to
  // the request's reply chain (see helpers/messageChain.ts). Always null for
  // an 'engagement' row: see FreeListingRequestRow in
  // free-listing-admin.service.ts for why that row has no anchor in this
  // sense.
  anchorMessageId: string | null;
  franchise: { id: string; company: string };
  consultant: { id: string; firstName: string; lastName: string };
}

export interface FreeListingStats {
  pendingRequests: number;
  freeListingFranchisors: number;
  respondedThisWeek: number;
  // Null when nothing was answered in the window — the card shows an em dash
  // rather than "0h", which would read as an instant response.
  avgResponseTimeHours: number | null;
}
